import * as React from "react"

import {GEOGRAPHY_OPTIONS} from "../constants"

import {replaceUnderscoresWithSpaces, smartRound} from "../utils";
import DataManager from "../DataManagers/DataManager";
import {
	EQUIVALENT_VALUE_COLOR,
	FULL_STATE_NAME_TO_ABBREVIATION,
	HIGHER_VALUE_COLOR,
	LOWER_VALUE_COLOR
} from "../DataManagers/utilsColorTheme";
import {isSignificantlyDifferent} from "../utilsData";


export const groupWordsIntoShortLines = lineOfText => {
	const maxCharactersPerLine = 9
	const groups = []
	const words = lineOfText.split(' ')
	let currWordGroup = ''
	words.forEach((word, index) => {
		currWordGroup += currWordGroup ? ' ' + word : word
		if (currWordGroup.length > maxCharactersPerLine) {
			groups.push(currWordGroup)
			currWordGroup = ''
		} else if (index === words.length - 1) { // Last word group may not be 9+ characters long
			groups.push(currWordGroup)
		}
	})
	return groups
}

export const renderSVGTextWithOneWordPerLine = d => {
	const lineSpacing = 14
	return (
		<text fontSize={10} textAnchor="middle" y={5} x={0}>
			{
				groupWordsIntoShortLines(replaceUnderscoresWithSpaces(d))
					.map((line, i) => <tspan key={i} x={0} y={5} dy={i*lineSpacing}>{line}</tspan>)
			}
		</text>
	)
}

export const pluralizeGeography = cartoLayerId => {
	switch(cartoLayerId) {
		case GEOGRAPHY_OPTIONS.county.cartoLayerId:
			return 'Counties'
		default:
			const name = Object.values(GEOGRAPHY_OPTIONS).find(geo => geo.cartoLayerId === cartoLayerId).geography
			return `${capitalize(name)}s`
	}
}

// rounds to visually sensible decimal place
export const smartDisplayValue = (num, min, max) => {
	if (typeof num === 'undefined' || num === null) return '-'
	if (typeof num === 'string') return num

	var val = smartRound(num, min || num, max || num)
	if (Math.abs(val) > 1000000000000000)
		return Math.round(val / 100000000000000) / 10.0 + 'Q'
	if (Math.abs(val) > 1000000000000)
		return Math.round(val / 100000000000) / 10.0 + 'T'
	if (Math.abs(val) > 1000000000)
		return Math.round(val / 100000000) / 10.0 + 'B'
	if (Math.abs(val) > 1000000)
		return Math.round(val / 100000) / 10.0 + 'M'
	if (Math.abs(val) > 1000)
		return Math.round(val / 100) / 10.0 + 'K'
	if (Math.round(val) !== val)
		return Math.round(val * 10) /10

	return val
}

export const capitalize = str => {
	return str ? str.replace(/(?:^|\s)\S/g, function(a) { return a.toUpperCase(); }) : str
}

const HUMAN_READABLE_LOOKUP = {
	namelsad: {text: 'Location', format: '_'},
	co_namelsad: {text: "OZ's Greater County", format: '_'},
	avg_commute_in_minutes_2017: {text: 'Avg. Commute (min)', format: '_'},
	under_poverty_level_pct_2017: {text: 'Poverty Rate', format: '_%'},
	total_population_2017: {text: 'Population', format: '_'},
	total_population_pct_change_2012_2017: {text: '5yr Pop. Change', format: '_%'},
	median_income_past_12_months_2017: {text: 'Median Income', format: '$_'},
	degree_4yr_or_more_pct_2017: {text: '% Pop. w/ 4yr Degree+', format: '_%'},
	highschool_dropout_rate_2017: {text: 'HS Dropout Rate', format: '_%'},
	pop_under_65_not_covered_by_health_insurance_pct_2017: {text: '% Uninsured (age<65)', format: '_%'},
	housing_vacancy_rate_total_2017: {text: 'Housing Vacancy', format: '_%'},
	median_gross_rent_2017: {text: 'Median Rent', format: '$_'},
	median_home_value_2017: {text: 'Median Home Value', format: '$_'},
	rural_area_pct_cbsa: {text: '% Rural (CBSA)', format: '_%'},
	rural_area_pct_cms: {text: '% Rural (CMS)', format: '_%'},
	rural_area_pct_ruca: {text: '% Rural (RUCA)', format: '_%'},
	rural_area_pct_uc: {text: '% Rural (UC)', format: '_%'},
	oz_blpct: {text: '% Opportunity Zones', format: '_%'},
	nmtc_blpct: {text: '% NMTC', format: '_%'},
	highed_lvl_4yr_5000up_blpct: {text: 'Near 4yr 5000+ Students', format: '_%'},
	highed_lvl_4yr_500up_blpct: {text: 'Near 4yr 500+ Students', format: '_%'},
	highed_lvl_4yr_blpct: {text: 'Near 4yr', format: '_%'},
	highed_lvl_2yr_blpct: {text: 'Near 2yr', format: '_%'},
	lodes_all_jobs_2017: {text: 'Total Jobs', format: '_'},
	rural_area_pct_forhp: {text: '% Rural (FORHP)', format: '_%'},
	rural_area_pct_rucc: {text: '% Rural (RUCC)', format: '_%'},
	rural_area_pct_usda: {text: '% Rural (USDA)', format: '_%'},
	f477_maxadup_2018jun_blpct: {text: '% Broadband > 0Mbps Up', format: '_%'},
	f477_maxadup_all_fiber_2018jun_blpct: {text: '% Fiber Access', format: '_%'},
	f477_maxadup_2018jun_5_blpct: {text: '% Broadband ≥ 50Mbps Up', format: '_%'},
	f477_maxadup_2018jun_50_blpct: {text: '% Broadband ≥ 50Mbps Up', format: '_%'},
	f477_maxadup_2018jun_200_blpct: {text: '% Broadband ≥ 200Mbps Up', format: '_%'},
	f477_maxadup_all_fiber_2018jun_max: {text: 'Broadband Max Fiber Up', format: '_'},
	f477_maxadup_2018jun_max: {text: 'Broadband Max Up', format: '_'},
	highed_lvl_4yr_2000up_blpct: {text: 'Near 4yr 2000+ Students', format: '_%'},
	trct_kffmn_inv_score: {text: 'Investment Score *', format: '_'},
	patents_core_index: {text: 'Patent Diffusion Index *', format: '_'},
	venture_capital_average_annual: {text: 'Avg. VC investment *', format: '_'},
	venture_capital_count_measures_core_index: {text: 'Avg. VC deals & IPOs *', format: '_'},
	venture_capital_deals_average_annual: {text: 'Avg. VC deals (#) *', format: '_'},
	venture_capital_dollar_measures_core_index: {text: 'Venture Dollars Index *', format: '_'},
	stem_degree_creation_per_1_000_population: {text: 'STEM Degree Creation *', format: '_'},
	lodes_all_jobs_pct_change_2012_2017: {text: '5yr Chg. in Total Jobs', format: '_%'},
	lodes_young_firm_rate_2017: {text: '% of Jobs with Young Firms', format: '_%'},
	lodes_young_firm_rate_ppc_2012_2017: {text: '5yr Chg. in Young Firm Rate', format: '_%'},
	lodes_ed_bachelor_high_rate_2017: {text: '% of Jobs w/ 4yr Degree+', format: '_%'},
	lodes_ed_bachelor_high_ppc_2012_2017: {text: '5yr Chg. in 4yr Deg. Rate', format: '_%'},
	lodes_unemploy_rate_2017: {text: 'Unemploy Rate', format: '_%'},
	lodes_unemploy_rate_ppc_2012_2017: {text: '5yr Chg. in Unemploy Rate', format: '_%'},
	age_working_pop_25_to_64_pct_2017: {text: '% Pop. of Working Age', format: '_%'},
	age_working_pop_25_to_64_ppc_change_2012_2017: {text: '5yr Chg. in Working Age Pop', format: '_%'},
	median_income_past_12_months_pct_change: {text: '5yr Chg. in Median Income', format: '_%'},
	youth_not_in_school_and_not_working_2017: {text: '% Disengaged Youth', format: '_%'},
}

export const humanReadableFields = fieldName => {
	if (fieldName in HUMAN_READABLE_LOOKUP)
		return HUMAN_READABLE_LOOKUP[fieldName].text
	return DataManager.stats.getDisplayName('attr_tract', fieldName)
}

export const humanReadableValue = (fieldName, fieldValue) => {
	if (fieldValue === '-')
		return fieldValue
	if (fieldName in HUMAN_READABLE_LOOKUP)
		return HUMAN_READABLE_LOOKUP[fieldName].format.replace('_', fieldValue)
	return fieldValue
}

const RURAL_BINARY_FLAGS_IN_RAW_DATA = {
	rural: ['YES', 'True', '1'],
	nonRural: ['NO', 'False', '0']
}
export const humanReadableRuralFilterOption = filterOptionString => {
	const _filterOptionString = `${filterOptionString}`
	if (RURAL_BINARY_FLAGS_IN_RAW_DATA.rural.includes(_filterOptionString)) {
		return 'Rural'
	}
	if (RURAL_BINARY_FLAGS_IN_RAW_DATA.nonRural.includes(_filterOptionString)) {
		return 'Non-rural'
	}
	return _filterOptionString
}

export const formatUSStatesArrayAsAbbreviatedSentence = (selectedUSStatesToFilterWithin) => {
	const abbreviatedStateNames = selectedUSStatesToFilterWithin.map(stateName => FULL_STATE_NAME_TO_ABBREVIATION[stateName])
	const numStates = abbreviatedStateNames.length
	if (numStates === 1) return abbreviatedStateNames[0]
	if (numStates === 2) return `${abbreviatedStateNames[0]} and ${abbreviatedStateNames[1]}`
	if (numStates > 9) return `${numStates} states`
	return abbreviatedStateNames.reduce((readableString, currentState, index) => index === numStates - 1 ? `${readableString}and ${currentState}` : `${readableString}${currentState}, `, '')
}


export const getColorStyleForCommunitySummaryComparisonValue = (localValue, referenceValue) => {
	return {
		color: isSignificantlyDifferent(localValue, referenceValue, 0.001)
			? ((referenceValue < localValue) ? LOWER_VALUE_COLOR : HIGHER_VALUE_COLOR)
			: EQUIVALENT_VALUE_COLOR
	}
}
